import { useMemo } from 'react'

import { useModals, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction, { type HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditBrandCustomProfileModal from
  '@modals/CreateOrEditBrandCustomProfileModal/CreateOrEditBrandCustomProfileModal'
import ManageBrandCustomProfileUserPermissionsModal from
  '@modals/ManageBrandCustomProfileUserPermissionsModal/ManageBrandCustomProfileUserPermissionsModal'
import ManageBrandSSOModal from '@modals/ManageBrandSSOModal'

import type { BrandModel } from '@models/brand'

import PageContent from './components/PageContent'

type DefaultState = {
  showCreateOrEditBrandCustomProfileModal: boolean,
  showManageBrandCustomProfileUserPermissionsModal: boolean,
  showManageBrandSSOModal: boolean,
}

type SetState = (state: Partial<DefaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditBrandCustomProfileModal: {
      closeModal: () => setState({ showCreateOrEditBrandCustomProfileModal: false }),
    },
    ManageBrandCustomProfileUserPermissionsModal: {
      createBrandCustomProfile: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeAllModals: () => setState({
        showManageBrandCustomProfileUserPermissionsModal: false,
        showCreateOrEditBrandCustomProfileModal: false,
      }),
      closeModal: () => setState({ showManageBrandCustomProfileUserPermissionsModal: false }),
      updateBrandCustomProfile: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageBrandSSOModal: {
      closeModal: () => setState({ showManageBrandSSOModal: false }),
      updateBrand: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const defaultState = {
  showCreateOrEditBrandCustomProfileModal: false,
  showManageBrandCustomProfileUserPermissionsModal: false,
  showManageBrandSSOModal: false,
}

const BrandSettings = (props: {brandId: number}) => {
  const { brandId } = props

  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditBrandCustomProfileModal,
    showManageBrandCustomProfileUserPermissionsModal,
    showManageBrandSSOModal,
  } = state

  const modalContext = useModals({ setState })
  const {
    topModalKey,
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showCreateOrEditBrandCustomProfileModal: (payload: { brand: BrandModel }) => {
        setModalData('CreateOrEditBrandCustomProfileModal', payload)
        setState({ showCreateOrEditBrandCustomProfileModal: true })
      },
      showManageBrandCustomProfileUserPermissionsModal: (payload: { brand: BrandModel }) => {
        setModalData('ManageBrandCustomProfileUserPermissionsModal', payload)
        setState({ showManageBrandCustomProfileUserPermissionsModal: true })
      },
      showManageBrandSSOModal: (payload: { brand: BrandModel }) => {
        setModalData('ManageBrandSSOModal', payload)
        setState({ showManageBrandSSOModal: true })
      },
      updateBrand: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }), [topModalKey])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent brandId={brandId} />

        <CreateOrEditBrandCustomProfileModal
          callbacks={callbacks('CreateOrEditBrandCustomProfileModal', setState)}
          showModal={showCreateOrEditBrandCustomProfileModal}
        />

        <ManageBrandSSOModal
          callbacks={callbacks('ManageBrandSSOModal', setState)}
          showModal={showManageBrandSSOModal}
        />

        <ManageBrandCustomProfileUserPermissionsModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageBrandCustomProfileUserPermissionsModal', setState)}
          showModal={showManageBrandCustomProfileUserPermissionsModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default BrandSettings
